import React from 'react';
import moment from 'moment';
import round from 'lodash/round';
import CircularProgress, {
  CircularProgressProps,
} from '@mui/material/CircularProgress';
import GeneralChart from '@/components/job-title/GeneralChart';
import DataInterpretationSEO from '@/components/DataInterpretationSEO';
import { DataTableColumnsType } from '@/components/job-title/DataTable';
import { JobTitleContext } from '@/context/create-context';
import {
  WORK_EXPERIENCE_0,
  WORK_EXPERIENCE_ARRAY,
  WORK_EXPERIENCE_FUTURE_SEO_MAP,
  WORK_EXPERIENCE_MAP,
} from '@/constants/job-title';

const Progress = (props: CircularProgressProps & { value: number }) => {
  const { value } = props;
  const approximateNum = Math.round(value);
  const percentNumber =
    approximateNum === 0 ? value.toFixed(1) : approximateNum;

  return (
    <div className="relative inline-flex">
      <CircularProgress
        className="!text-gray-100"
        variant="determinate"
        value={100}
        size={48}
        thickness={5}
      />
      <CircularProgress
        className="!text-dark-cyan/70 !absolute !left-0"
        variant="determinate"
        value={value}
        size={48}
        thickness={5}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-opacity-85 text-xs">{`${percentNumber}%`}</span>
      </div>
    </div>
  );
};

export const columns: DataTableColumnsType = [
  {
    headerName: '工作年限',
    field: 'experience',
    align: 'left',
  },
  {
    headerName: '月度新增职位数',
    field: 'amount',
    align: 'center',
    renderCell: (data) => {
      return <span className="font-bold">{data}</span>;
    },
  },
  {
    headerName: '职位占比数',
    field: 'percentage',
    align: 'center',
    renderCell: (data) => {
      return <Progress value={+(data ?? 0)} />;
    },
  },
];

const JobFutureExperienceCard = () => {
  const {
    jobTitle: {
      job_open_details: {
        new_job_details: {
          last_month_by_experience: lastMonthByExperience,
          last_sorted_month_by_experience: lastSortedMonthByExperience,
        },
      },
      city_name: cityName = '',
    },
    displayName,
  } = React.useContext(JobTitleContext);

  const experienceData = React.useMemo(() => {
    const total =
      lastSortedMonthByExperience.length > 0
        ? lastSortedMonthByExperience[0].value.reduce(
            (sum: number, next: { value: number }) => sum + next.value,
            0,
          )
        : 0;
    return {
      data:
        total === 0
          ? []
          : lastSortedMonthByExperience[0].value.sort(
              (
                first: { name: string; value: number },
                second: { name: string; value: number },
              ) =>
                WORK_EXPERIENCE_ARRAY.indexOf(first.name) -
                WORK_EXPERIENCE_ARRAY.indexOf(second.name),
            ),
      total,
    };
  }, [lastSortedMonthByExperience]);

  const seoValue = React.useMemo(() => {
    const data = experienceData.data.map(
      ({ name, value }: { name: string; value: number }) => {
        const radio = round((value / (experienceData.total || 1)) * 100);
        return `${
          radio
            ? `面向${WORK_EXPERIENCE_FUTURE_SEO_MAP[name]}${
                name === WORK_EXPERIENCE_0 ? '开放的职位数' : '职位开放数'
              }为${value}个，占比为${radio}%`
            : `几乎没有面向${WORK_EXPERIENCE_FUTURE_SEO_MAP[name]}的职位`
        }`;
      },
    );

    return {
      data,
      lastDate:
        lastMonthByExperience.length > 0
          ? moment(lastMonthByExperience[0].name).format('yyyy年MM月')
          : '',
    };
  }, [experienceData, lastMonthByExperience]);

  const rows = React.useMemo(() => {
    return experienceData.data.map((item: { name: string; value: number }) => ({
      experience: WORK_EXPERIENCE_MAP[item.name],
      amount: item.value,
      percentage: (item.value / (experienceData.total || 1)) * 100,
    }));
  }, [experienceData]);

  return (
    <GeneralChart
      type="dataTable"
      title={`${cityName}不同工作年限的${displayName}工作机会占比`}
      chartRootClass="[&_.MuiTableCell-alignLeft]:pl-0 tablet:[&_.MuiTableCell-alignLeft]:pl-8"
      config={{
        columns,
        rows,
      }}
      seoInfo={
        seoValue.data.length ? (
          <div className="text-xs mt-4">
            <DataInterpretationSEO
              lineClamp="line-clamp-5 tablet:line-clamp-3"
              className="font-normal text-opacity-85"
            >
              {`工作年限不同，${cityName}${displayName}的工作机会是否相同呢？${seoValue.data.join(
                '；',
              )}。数据截止于${
                seoValue.lastDate
              }，数据来源依据于各平台公开薪酬数据，仅供参考。`}
            </DataInterpretationSEO>
          </div>
        ) : undefined
      }
    />
  );
};

export default JobFutureExperienceCard;
